import { mmFetchWithResponse } from './mmFetchWithResponse';

/**
 * Page size used by the bulk catalog endpoints.
 *
 * These responses used to be fetched in one request, which exhausted the API's 512MB PHP memory limit once
 * the catalog grew past roughly 25k items. Paging keeps the server's peak memory proportional to this number
 * instead of to the size of the catalog.
 */
export const ITEM_PAGE_SIZE = 5000;

/** Stops a server that keeps handing back a nextPage from hanging the browser forever. */
const MAX_PAGES = 200;

/**
 * Fetch every page of a paginated list endpoint and return a single response with the rows concatenated.
 *
 * The returned object keeps the same shape the endpoint returns for a single request, so callers cannot tell
 * that paging happened.
 *
 * @param {(page: number, pageSize: number) => string} buildUrl builds the request URL for a given page
 * @param {string} dataKey key under `data` holding the rows, e.g. 'items' or 'rows'
 * @param {number} [pageSize]
 * @returns {Promise<Object>} the last response, with every page's rows merged under `dataKey`
 */
export const fetchAllPages = async (buildUrl, dataKey, pageSize = ITEM_PAGE_SIZE) => {
    const allRows = [];
    let page = 0;

    for (let requestCount = 0; requestCount < MAX_PAGES; requestCount += 1) {
        // eslint-disable-next-line no-await-in-loop -- each page's URL depends on the previous page's nextPage
        const response = await mmFetchWithResponse(buildUrl(page, pageSize));

        if (!response?.status) {
            // Propagate the failure untouched so callers keep their existing error handling.
            return response;
        }

        allRows.push(...(response.data?.[dataKey] ?? []));

        const { nextPage } = response.data?.page ?? {};

        // A null nextPage means this was the last page. Anything that fails to advance is treated the same
        // way rather than looping: better a short result than a hung tab.
        if (typeof nextPage !== 'number' || nextPage <= page) {
            return { ...response, data: { ...response.data, [dataKey]: allRows } };
        }

        page = nextPage;
    }

    // Return a failure rather than throwing: callers such as catalog/components/itemInformation.jsx check
    // `res.status` without a try/catch, matching the mmFetchWithResponse contract. Throwing here would
    // surface as an unhandled rejection instead of the error state they already render.
    return {
        status: false,
        msg: `Stopped after ${MAX_PAGES} pages fetching ${dataKey}. Contact engineering.`,
        data: {},
        statusCode: null,
    };
};
